import { useState, useEffect, useMemo } from 'react';
import { useLocale } from 'next-intl';

import { AccountActStatus } from '@nf/utils-common/constants';
import { useAuthToken } from '@/store/auth';
import { correctScoreSpreadAssetService } from '@/features/promotion-activity-modal/services/correct-score-spread-asset.service';
import { switchViewService } from '@/services/swtich-view.service';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { useAccountInfo } from '@/hooks/query/account';
import { usePredictionMarket } from '@/hooks/use-prediction-market';
import { useBannerInfo, type BannerRawItem } from '@/hooks/query/use-banner-data';
import { useBannerNavigation } from './use-banner-navigation';

import { SiteConfigsSettingsData } from '@nf/types/site';
import { CheckIsEnabledParameters } from '../types';

type BannerImage = {
	href: string;
	webpHref: string;
	fallbackHref?: string;
	fallbackWebpHref?: string;
	useImageServer?: boolean;
	inGalaxyFolder?: boolean;
	/** href is already a full URL: skip image server path and version id */
	isFinalUrl?: boolean;
};

export type AdvertisementItem = {
	name: string;
	image: BannerImage;
	enabled?: boolean;
	actType?: string;
	navigateKey?: string;
	specifiedOrder?: number;
	refinedAnalyseKey?: string;
	trackTagName?: string;
};

export const toAdvertisementItem = (item: BannerRawItem, shortCode: string): AdvertisementItem => {
	const imageHref = item.imgIsFinal
		? item.Img.replace('{0}', shortCode)
		: `public/afterBanner/${shortCode}/Images/Banners/${item.Img.replace(/(\?v=).*/, '')}`;

	const advertisementItem: AdvertisementItem = {
		name: item.KeyId,
		image: {
			href: imageHref,
			webpHref: imageHref,
			useImageServer: true,
			isFinalUrl: item.imgIsFinal
		}
	};
	if (item.enable) {
		advertisementItem.enabled = /^true$/i.test(item.enable);
	}
	if (item.navigateKey) {
		advertisementItem.navigateKey = `${item.navigateKey}`;
	}
	if (item.actType) {
		advertisementItem.actType = `${item.actType}`;
	}
	return advertisementItem;
};

export const useBannerList = () => {
	const locale = useLocale();
	const { SiteConfigsData } = useGetSiteConfigs();
	const { enableESoccerBattlePromotion } = useLicSettings();
	const { user } = useAccountInfo();
	const { enablePredictionMarketBanner } = usePredictionMarket();
	const authToken = useAuthToken();
	const isBefore = !authToken;
	const { bannerItems } = useBannerInfo();
	const { checkIsEnabled } = useBannerNavigation();
	const [customAdvertisements, setCustomAdvertisements] = useState<AdvertisementItem[]>([]);

	const shortCode = useMemo(() => {
		return switchViewService.convertLanguageToShortCode(
			user?.Lang as string, 
			SiteConfigsData as SiteConfigsSettingsData
		);
	}, [user?.Lang, SiteConfigsData]);

	useEffect(() => {
		if (!SiteConfigsData?.EnableImageServer) {
			return;
		}
		try {
			setCustomAdvertisements(bannerItems.map(item => toAdvertisementItem(item, shortCode)));
		} catch {
			setCustomAdvertisements([]);
		}
	}, [SiteConfigsData?.EnableImageServer, shortCode, bannerItems]);

	const advertisementList = useMemo(() => {
		const imageServerEnabled = SiteConfigsData?.EnableImageServer;
		const siteId = SiteConfigsData?.GetMessageID ?? '0';

		const advertisementItems: AdvertisementItem[] = [
			{
				name: 'itcbet2',
				image: {
					href: '/images/jupiter/42024/banners/en/2.png',
					webpHref: '/images/jupiter/42024/banners/en/2.webp',
					useImageServer: imageServerEnabled,
					inGalaxyFolder: true
				},
				navigateKey: 'itcbet2',
				specifiedOrder: 0,
			},
			{
				name: 'itcbet3',
				image: {
					href: '/images/jupiter/42024/banners/en/3.png',
					webpHref: '/images/jupiter/42024/banners/en/3.webp',
					useImageServer: imageServerEnabled,
					inGalaxyFolder: true
				},
				navigateKey: 'itcbet3',
				specifiedOrder: 0,
			},
			{
				name: 'itcbet4',
				image: {
					href: '/images/jupiter/42024/banners/en/4.png',
					webpHref: '/images/jupiter/42024/banners/en/4.webp',
					useImageServer: imageServerEnabled,
					inGalaxyFolder: true
				},
				navigateKey: 'itcbet4',
				specifiedOrder: 0,
			},
			{
				name: 'itcbet5',
				image: {
					href: '/images/jupiter/42024/banners/en/5.png',
					webpHref: '/images/jupiter/42024/banners/en/5.webp',
					useImageServer: imageServerEnabled,
					inGalaxyFolder: true
				},
				navigateKey: 'itcbet5',
				specifiedOrder: 0,
			},
			{
				name: 'itcbet6',
				image: {
					href: '/images/jupiter/42024/banners/en/6.png',
					webpHref: '/images/jupiter/42024/banners/en/6.webp',
					useImageServer: imageServerEnabled,
					inGalaxyFolder: true
				},
				navigateKey: 'itcbet6',
				specifiedOrder: 0,
			},
			{
				name: 'itcbet7',
				image: {
					href: '/images/jupiter/42024/banners/en/7.png',
					webpHref: '/images/jupiter/42024/banners/en/7.webp',
					useImageServer: imageServerEnabled,
					inGalaxyFolder: true
				},
				navigateKey: 'itcbet7',
				specifiedOrder: 0,
			},
			{
				name: 'macaubet-new-member',
				image: {
					href: '/images/jupiter/42018/banners/en/pm_macaubet-bonus-turnover.jpg',
					webpHref: '/images/jupiter/42018/banners/en/pm_macaubet-bonus-turnover.webp',
					useImageServer: true,
					inGalaxyFolder: true,
				},
				navigateKey: 'macaubet-new-member',
				specifiedOrder: 0,
			},
			{
				name: 'macaubet-slot2',
				image: {
					href: '/images/jupiter/42018/banners/en/macaubet-slot2.jpg',
					webpHref: '/images/jupiter/42018/banners/en/macaubet-slot2.webp',
					useImageServer: true,
					inGalaxyFolder: true,
				},
				navigateKey: 'macaubet-slot2',
				specifiedOrder: 0,
			},
			{
				name: 'macaubet-sports',
				image: {
					href: '/images/jupiter/42018/banners/en/macaubet-sports.jpg',
					webpHref: '/images/jupiter/42018/banners/en/macaubet-sports.webp',
					useImageServer: true,
					inGalaxyFolder: true,
				},
				navigateKey: 'macaubet-sports',
				specifiedOrder: 0,
			},
			{
				name: 'macaubet-slot',
				image: {
					href: '/images/jupiter/42018/banners/en/macaubet-slot.jpg',
					webpHref: '/images/jupiter/42018/banners/en/macaubet-slot.webp',
					useImageServer: true,
					inGalaxyFolder: true,
				},
				navigateKey: 'macaubet-slot',
				specifiedOrder: 0,
			},
			{
				name: 'streamer-schedule-banner',
				image: {
					href: `/images/banner/home/${shortCode}/kv_streamer-schedule-lobby.jpg`,
					webpHref: `/images/banner/home/${shortCode}/kv_streamer-schedule-lobby.webp`,
					useImageServer: true,
					inGalaxyFolder: true,
				},
				navigateKey: 'streamer-schedule-banner',
				enabled: !isBefore && 
					Boolean(user?.ActStatus === AccountActStatus.Deposited) && 
					Boolean(SiteConfigsData?.EnableStreamerSchedule),
				specifiedOrder: 8,
			},
			{
				name: 'e-soccer-virtual',
				image: {
					href: `/images/banner/home/${shortCode}/kv_e-soccer-virtual.jpg`,
					webpHref: `/images/banner/home/${shortCode}/kv_e-soccer-virtual.webp`,
					useImageServer: imageServerEnabled,
					inGalaxyFolder: true
				},
				navigateKey: 'e-soccer-virtual',
				specifiedOrder: 6
			},
			{
				name: 'nba-2k-virtual',
				image: {
					href: `/images/banner/home/${shortCode}/kv_nba-2k-virtual.jpg`,
					webpHref: `/images/banner/home/${shortCode}/kv_nba-2k-virtual.webp`,
					useImageServer: imageServerEnabled,
					inGalaxyFolder: true
				},
				navigateKey: 'nba-2k-virtual',
				specifiedOrder: 7
			},
			{
				name: 'prediction-market-banner',
				image: {
					href: `/images/banner/lobby/prediction-market/prediction-market-${shortCode}.jpg`,
					webpHref: `/images/banner/lobby/prediction-market/prediction-market-${shortCode}.webp`,
					fallbackHref: '/images/banner/lobby/prediction-market/prediction-market-en.jpg',
					fallbackWebpHref: '/images/banner/lobby/prediction-market/prediction-market-en.webp',
					useImageServer: imageServerEnabled,
					inGalaxyFolder: true
				},
				navigateKey: 'prediction-market-banner',
				specifiedOrder: 1,
				enabled: Boolean(enablePredictionMarketBanner)
			},
			{
				name: 'better-esoccer-battle',
				image: {
					href: `/images/banner/home/${shortCode}/kv_better-esoccer-battle.jpg`,
					webpHref: `/images/banner/home/${shortCode}/kv_better-esoccer-battle.webp`,
					useImageServer: imageServerEnabled,
					inGalaxyFolder: true
				},
				navigateKey: 'better-esoccer-battle',
				trackTagName: 'lobby-carousel-banner-112',
				enabled: enableESoccerBattlePromotion,
				specifiedOrder: 3,
				refinedAnalyseKey: 'lobby-carousel-banner-112'
			},
			...customAdvertisements,
		];

		const webSkinTypeAllow = !SiteConfigsData?.WebSkinTypeAllow
			? []
			: SiteConfigsData.WebSkinTypeAllow.split(/\s*,\s*/).map(skin => Number(skin));
		const parameters: CheckIsEnabledParameters = { siteId, webSkinTypeAllow, user, isBefore };

		const checkAdvertisementEnabled = (advertisementItem: AdvertisementItem): boolean => {
			if (advertisementItem.enabled !== undefined) {
				return advertisementItem.enabled;
			}
			if (!advertisementItem.navigateKey) {
				return true;
			}

			return checkIsEnabled(advertisementItem.navigateKey, parameters);
		};
		
		const sortAdvertisementsWithSpecifiedOrder = (adA: AdvertisementItem, adB: AdvertisementItem): number => {
			const hasValidOrderA = adA.specifiedOrder !== undefined && Number.isInteger(adA.specifiedOrder);
			const hasValidOrderB = adB.specifiedOrder !== undefined && Number.isInteger(adB.specifiedOrder);
			return !hasValidOrderA && !hasValidOrderB 
				? 0
				: (hasValidOrderA && hasValidOrderB) 
					? (Number(adA.specifiedOrder) - Number(adB.specifiedOrder))
					: hasValidOrderA ? -1 : 1;
		};

		return advertisementItems
			.filter(checkAdvertisementEnabled)
			.sort(sortAdvertisementsWithSpecifiedOrder);
	}, [
		SiteConfigsData, 
		locale,
		isBefore, 
		user, 
		customAdvertisements, 
		shortCode,
		checkIsEnabled, 
		enableESoccerBattlePromotion,
		enablePredictionMarketBanner
	]);
	
	return advertisementList;
};